// src/pages/AdminLogin.jsx
import React, { useState } from 'react';
import { motion } from "framer-motion";
import { supabase } from "../lib/supabaseClient";
import { useNavigate } from "react-router-dom";
import { LogIn, Mail, Lock, Loader2, Eye, EyeOff } from 'lucide-react';
import logo from '../assets/haibeirut_logo.png';
import restaurantBackground from '../assets/bg_image.png';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email,
        password: password,
      });

      if (error) throw error;

      // Check if user exists in admin_profiles table
      const { data: adminProfile, error: profileError } = await supabase
        .from('admin_profiles')
        .select('*')
        .eq('id', data.user.id)
        .single();

      if (profileError || !adminProfile) {
        // User is authenticated but not an admin - sign them out
        await supabase.auth.signOut();
        throw new Error('Unauthorized access. Admin privileges required.');
      }

      // Success - redirect to admin dashboard
      navigate('/admin-dashboard', { replace: true });
      
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToHome = () => {
    navigate('/', { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 overflow-hidden">
        <img 
          src={restaurantBackground}
          alt="Restaurant Background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0C2418]/90 via-[#1A3C27]/80 to-[#2D6B47]/70"></div>
      </div>

      {/* Login Form */}
      <div className="flex-1 flex items-center justify-center px-4 py-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Logo */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              className="relative mx-auto mb-4"
            >
              <img 
                src={logo}
                alt="Hai Beirut Logo"
                className="w-24 h-24 object-contain mx-auto"
              />
            </motion.div>
            <h1 className="text-2xl text-white font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
              Admin Access
            </h1>
            <p className="text-white/80 text-sm mt-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Secure area - Authorized personnel only
            </p>
          </div>

          {/* Login Card */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-[#4CAF50]/20 to-[#2D6B47]/20 rounded-2xl blur-xl"></div>
            
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8 border border-white/20 relative">
              <form onSubmit={handleLogin} className="space-y-6">
                {/* Error Message */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-red-50 text-red-700 rounded-lg border border-red-200 text-sm"
                  >
                    {error}
                  </motion.div>
                )}

                {/* Email Field */}
                <div>
                  <label className="block text-[#1A3C27] font-medium mb-2 text-sm" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#7A9C89] w-5 h-5" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="admin@haibeirut.com"
                      className="w-full pl-10 pr-4 py-3 bg-white/80 border border-[#D1E7DD] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2D6B47]/40 focus:border-[#2D6B47] transition-all"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div>
                  <label className="block text-[#1A3C27] font-medium mb-2 text-sm" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#7A9C89] w-5 h-5" />
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      className="w-full pl-10 pr-12 py-3 bg-white/80 border border-[#D1E7DD] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2D6B47]/40 focus:border-[#2D6B47] transition-all"
                      required
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#7A9C89] hover:text-[#2D6B47] transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Submit Button */}
                <motion.button
                  type="submit"
                  disabled={loading}
                  whileHover={{ scale: loading ? 1 : 1.02 }}
                  whileTap={{ scale: loading ? 1 : 0.98 }}
                  className="w-full py-3 bg-gradient-to-r from-[#2D6B47] to-[#4CAF50] text-white font-semibold rounded-xl shadow-lg shadow-[#2D6B47]/30 hover:shadow-xl transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Verifying credentials...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center">
                      <LogIn className="w-5 h-5 mr-2" />
                      Login to Dashboard
                    </span>
                  )}
                </motion.button>

                {/* Back to Home Link */}
                <div className="text-center mt-4">
                  <button
                    type="button"
                    onClick={handleBackToHome}
                    className="text-sm text-[#2D6B47] hover:underline focus:outline-none transition-colors"
                  >
                    ← Return to Homepage
                  </button>
                </div>
              </form>

              {/* Security Note */}
              <div className="mt-6 pt-6 border-t border-[#E5F0E8]">
                <p className="text-xs text-center text-[#7A9C89]">
                  This area is restricted to authorized personnel only.<br />
                  All access is logged and monitored.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}